"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const expertiseData = [{
  title: "Sell-Side M&A Advisory",
  description: "We specialize in representing business owners in the $2M–$30M EBITDA range, ensuring your legacy is preserved while maximizing enterprise value through competitive bidding and strategic positioning.",
  subheading: "Deal Highlight: $108M F&B Acquisition",
  specs: ["Comprehensive business valuation & modeling", "Confidential marketing to qualified buyers", "Rigorous due diligence management", "Expert negotiation of deal structures"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657590/img/m_and_a_boardroom_ocean.png",
  link: "https://bpvconsulting.com/ma-advisory-services/",
  linkText: "Explore M&A Services"
}, {
  title: "Strategic Capital Raises",
  description: "Fuel your next phase of growth with tailored capital solutions. We connect high-potential firms with private equity, family offices, and institutional lenders to secure optimal funding terms.",
  subheading: "Deal Highlight: $100M Working Capital Line of Credit",
  specs: ["Equity and debt structure optimization", "Access to exclusive private capital networks", "Preparation of institutional-grade decks", "Strategic alignment with long-term partners"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657590/img/capital_raise_california_coast.png",
  link: "https://bpvconsulting.com/capital-raises/",
  linkText: "View Capital Solutions"
}, {
  title: "Strategic Advisory",
  description: "Beyond the transaction, we provide high-level guidance on merger integration, operational optimization, growth strategy, and market expansion to ensure long-term enterprise value.",
  subheading: "Deal Highlight: Market Entry Strategy",
  specs: ["Market entry strategy and feasibility analysis", "Operational optimization roadmap", "Integration planning and execution support", "Post-close value creation guidance"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657590/img/investment_mandate_luxury_office.png",
  link: "/StrategicAdvisoryPage",
  linkText: "Learn About Strategic Advisory"
}];
function ServiceRow({
  item,
  index
}: {
  item: typeof expertiseData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} id={index === 0 ? "strategic-solutions" : undefined} data-index={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`space-y-8 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
        <div>
          <h2 className="text-3xl md:text-4xl font-default font-bold text-primary mb-4">
            {item.title}
          </h2>
          <p className="text-muted-foreground text-lg font-serif leading-relaxed">
            {item.description}
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-md border border-ring/20 rounded-sm p-6 md:p-8 shadow-lg">
          <h3 className="text-xl font-default font-semibold text-primary-foreground mb-6">
            {item.subheading}
          </h3>
          <ul className="space-y-4">
            {item.specs.map((spec, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                <CircleCheck className="w-5 h-5 text-primary-foreground shrink-0 mt-0.5" />
                <span className="text-foreground font-serif">{spec}</span>
              </li>)}
          </ul>
        </div>

        <div>
          <Button asChild variant="default" size="lg" className="font-mono font-medium tracking-widest uppercase bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
            <Link to={item.link} target={item.link.startsWith('http') ? "_blank" : undefined} rel={item.link.startsWith('http') ? "noopener noreferrer" : undefined}>
              {item.linkText}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>

      {}
      <div className={`relative ${isEven ? "lg:order-2" : "lg:order-1"}`}>
        <div className="relative rounded-sm overflow-hidden aspect-[4/3] shadow-2xl border border-border/30 group">
          <div className="absolute inset-0 bg-secondary/10 group-hover:bg-transparent transition-colors duration-500 z-10" />
          <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
          {}
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary-foreground/10 blur-3xl rounded-full z-0 pointer-events-none" />
        </div>
      </div>
    </div>;
}
export default function OnzeExpertise() {
  return <section data-section-id="224818" id="strategic-solutions" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="text-sm font-mono font-bold tracking-widest text-primary-foreground uppercase mb-3">
            Strategic Solutions
          </h2>
          <p className="text-4xl md:text-5xl font-default font-bold text-primary mb-6">
            Institutional Expertise, Coastal Perspective
          </p>
          <div className="w-20 h-0.5 bg-primary-foreground mx-auto mb-6" />
          <p className="text-lg text-muted-foreground font-serif">
            Blue Phoenix Ventures combines the rigor of Wall Street with the agility of a boutique firm to deliver exceptional outcomes for business owners and investors.
          </p>
        </div>

        {}
        <div className="space-y-24 md:space-y-40">
          {expertiseData.map((item, index) => <ServiceRow key={index} item={item} index={index} />)}
        </div>
      </div>
    </section>;
}